import React, { useState } from 'react';
import Layout from '../components/Layout';
import WhiteboardCanvas from '../components/WhiteboardCanvas';
import WhiteboardToolbar from '../components/WhiteboardToolbar';
import { DrawingElement, Point, Tool } from '../types/whiteboard';
import '../styles/Whiteboard.css';
import { useNavigate } from 'react-router-dom';

const CustomWhiteboard: React.FC = () => {
    const navigate = useNavigate();
    const [elements, setElements] = useState<DrawingElement[]>([]);
    const [currentTool, setCurrentTool] = useState<Tool>('select');
    const [currentColor, setCurrentColor] = useState('#000000');
    const [currentStrokeWidth, setCurrentStrokeWidth] = useState(2);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState<Point>({ x: 0, y: 0 });

    const handleAddElement = (element: DrawingElement) => {
        setElements(prev => [...prev, element]);
    };

    const handleUpdateElement = (id: string, updates: Partial<DrawingElement>) => {
        setElements(prev =>
            prev.map(el => (el.id === id ? { ...el, ...updates } : el))
        );
    };

    const handleDeleteElement = (id: string) => {
        setElements(prev => prev.filter(el => el.id !== id));
    };

    const handleClear = () => {
        if (window.confirm('Are you sure you want to clear the whiteboard?')) {
            setElements([]);
        }
    };

    const handleUndo = () => {
        if (elements.length > 0) {
            setElements(prev => prev.slice(0, -1));
        }
    };

    const handleExport = () => {
        const canvas = document.querySelector('.whiteboard-canvas') as HTMLCanvasElement;
        if (canvas) {
            const link = document.createElement('a');
            link.download = 'whiteboard.png';
            link.href = canvas.toDataURL();
            link.click();
        }
    };

    const handleFitToScreen = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    return (
        <Layout>
            <div className="whiteboard-container">
                <div className="whiteboard-header">
                    <div className="whiteboard-title-section">
                        <button className="back-button" onClick={() => navigate('/whiteboard')}>
                            ← Back to Whiteboards
                        </button>
                        <input
                            type="text"
                            className="whiteboard-name-input"
                            defaultValue="Custom Whiteboard"
                            placeholder="Whiteboard name"
                        />
                    </div>
                    <div className="whiteboard-header-actions">
                        <span className="element-count">{elements.length} elements</span>
                        <button className="header-button">
                            💾 Save
                        </button>
                    </div>
                </div>

                <div className="whiteboard-workspace">
                    <WhiteboardToolbar
                        currentTool={currentTool}
                        currentColor={currentColor}
                        currentStrokeWidth={currentStrokeWidth}
                        zoom={zoom}
                        onToolChange={setCurrentTool}
                        onColorChange={setCurrentColor}
                        onStrokeWidthChange={setCurrentStrokeWidth}
                        onZoomChange={setZoom}
                        onFitToScreen={handleFitToScreen}
                        onClear={handleClear}
                        onUndo={handleUndo}
                        onExport={handleExport}
                    />

                    <div className="whiteboard-canvas-container">
                        <WhiteboardCanvas
                            elements={elements}
                            currentTool={currentTool}
                            currentColor={currentColor}
                            currentStrokeWidth={currentStrokeWidth}
                            zoom={zoom}
                            pan={pan}
                            onAddElement={handleAddElement}
                            onUpdateElement={handleUpdateElement}
                            onPanChange={setPan}
                            onDeleteElement={handleDeleteElement}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CustomWhiteboard;
